/** Longest order note accepted, matching the textarea's `maxLength`. */
export const CUSTOMER_NOTES_MAX_LENGTH = 2000;

/**
 * Drops the per-entry `notes` from dietary requirements before they are sent to
 * Collins. The order note is held mirrored on every entry for the dietary
 * modal, but Collins stores it once as the pre-order's `customer_notes`; Collins
 * copies each dietary entry verbatim onto the booking's and the pre-order's
 * `dietary_requirements`, so a `notes` key left on the entries would be stored
 * there too.
 */
export function withoutDietaryNotes<T extends { notes?: string }>(requirements: readonly T[]): Omit<T, "notes">[] {
  return requirements.map(({ notes: _notes, ...requirement }) => requirement);
}

/**
 * Mirrors an order note onto every dietary requirement entry — the shape the
 * dietary modal and confirm step read the note from. With no entries, a single
 * unnamed entry carries it.
 */
export function withDietaryNote<T extends object>(
  requirements: readonly T[],
  note: string,
): Array<T & { notes: string }> | Array<{ name: string; diet_types: string[]; allergies: string[]; notes: string }> {
  if (requirements.length === 0) {
    return [{ name: "", diet_types: [], allergies: [], notes: note }];
  }
  return requirements.map((requirement) => ({ ...requirement, notes: note }));
}

/**
 * Names each dietary requirement after the guest at the same position in
 * `forNames`. Requirements are stored positionally, but their `name` is only
 * captured when the dietary modal is saved — a name added or edited afterwards
 * would otherwise leave the entry unnamed or carrying the old name, and Collins
 * pairs requirements with items by matching that name to the item's `for_name`.
 *
 * Entries without a matching name, and every entry when no names are being
 * ordered for, keep the name they already have.
 */
export function withDietaryNamesFromForNames<T extends { name?: string }>(
  requirements: readonly T[],
  forNames: readonly string[],
): T[] {
  return requirements.map((requirement, index) =>
    index < forNames.length ? { ...requirement, name: forNames[index] } : requirement,
  );
}

/**
 * Reorders dietary requirements loaded from an existing order so each entry
 * sits at its guest's position in `forNames`.
 *
 * Requirements are stored and rendered positionally, but an order being edited
 * returns them in Collins' order while `forNames` is rebuilt from the items'
 * `for_name` order. Entries are matched to guests by `name`; guests without a
 * matching entry take the next unmatched one, and any left over keep their
 * relative order at the end, where the positional readers ignore them.
 */
export function alignDietaryRequirementsToForNames<T extends { name?: string }>(
  requirements: readonly T[],
  forNames: readonly string[],
): Array<T | { name: string; diet_types: string[]; allergies: string[] }> {
  if (forNames.length === 0) return [...requirements];

  const unmatched = [...requirements];
  const aligned = forNames.map((name) => {
    const index = unmatched.findIndex((requirement) => requirement.name === name);
    return index === -1 ? undefined : unmatched.splice(index, 1)[0];
  });

  const result: Array<T | { name: string; diet_types: string[]; allergies: string[] }> = [];
  for (const [index, name] of forNames.entries()) {
    result.push(aligned[index] ?? unmatched.shift() ?? { name, diet_types: [], allergies: [] });
  }
  return [...result, ...unmatched];
}
